import React, { useEffect, useState } from "react";
import {
    Button,
    Input,
    Textarea,
    Label,
    Spinner,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    Divider,
    Combobox,
    Option,
    OptionGroup,
    Checkbox,
} from "@fluentui/react-components";
import axios from "axios";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { Base_Url, auth, db } from "./services/firebase";
import { notify } from "./components/AppNotifications";
import Layout from "./components/Layout";

/**
 * Send an instant push + in-app message to every member.
 *
 * The send itself is server-side (/api/sendBroadcast): a browser cannot publish
 * to an FCM topic and must never hold the FCM credential. This screen only
 * composes, confirms, and shows history.
 */
export default function Broadcast() {
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    // Audience: who receives this. Options come from the backend so the Klaviyo
    // lists/segments are always the live ones, not a hardcoded copy.
    const [audienceOptions, setAudienceOptions] = useState(null);
    const [audience, setAudience] = useState({ type: "all" });
    const [reach, setReach] = useState(null);
    const [audienceQuery, setAudienceQuery] = useState("");
    const [loadingReach, setLoadingReach] = useState(false);
    // Klaviyo sync. Both passes also run nightly; these let marketing force one
    // instead of waiting, which is the only way to tell a sync problem from a
    // timing problem.
    const [syncing, setSyncing] = useState(null);      // "members" | "rsvps" | "eventSegments" | "memberDirectory" | null
    const [syncDryRun, setSyncDryRun] = useState(false);
    const [syncResult, setSyncResult] = useState(null); // { kind, dryRun, at, summary }
    // App build adoption. Loaded on demand rather than on mount: it reads every
    // member record, and this screen's job is sending, not reporting.
    const [adoption, setAdoption] = useState(null);
    const [loadingAdoption, setLoadingAdoption] = useState(false);

    useEffect(() => { loadHistory(); loadAudienceOptions(); }, []);
    // Re-check reach whenever the audience changes, so the count shown is always
    // for the currently selected audience.
    useEffect(() => { previewReach(audience); }, [audience]);

    async function authHeaders() {
        const token = await auth.currentUser?.getIdToken();
        return { Authorization: `Bearer ${token}` };
    }

    // Force a Klaviyo reconcile. The portal only triggers and reports; the pass
    // itself is server-side (it holds the Klaviyo key and is admin-gated there).
    //
    // eventSegments is NOT one of the nightly passes. New events get their
    // Klaviyo segment from the Events trigger the moment they are created; this
    // is the catch-up for events that already existed when that shipped. It is
    // safe to re-run — a segment that exists and is correctly named costs no
    // API call — but it is a backfill, not a sync, and the copy says so.
    const SYNCS = {
        members: { path: "reconcileMembers", label: "member list" },
        rsvps: { path: "reconcileRsvpProfiles", label: "event RSVP profiles" },
        eventSegments: { path: "backfillEventSegments", label: "event segments" },
        memberDirectory: { path: "backfillMemberDirectory", label: "member directory" },
    };

    async function runSync(kind) {
        const cfg = SYNCS[kind];
        setSyncing(kind);
        setSyncResult(null);
        try {
            const res = await axios.post(
                `${Base_Url}/api/${cfg.path}`,
                { dryRun: syncDryRun },
                { headers: await authHeaders() }
            );
            const summary = (res.data && res.data.data) || {};
            setSyncResult({ kind, dryRun: syncDryRun, at: new Date(), summary });
            notify(
                syncDryRun
                    ? `Dry run finished — nothing was written to the ${cfg.label}.`
                    : `Synced the ${cfg.label}.`,
                "success"
            );
        } catch (e) {
            const msg =
                (e.response && e.response.data && e.response.data.error && e.response.data.error.message) ||
                e.message || "Sync failed";
            setSyncResult({ kind, dryRun: syncDryRun, at: new Date(), error: msg });
            notify(`Could not sync the ${cfg.label}: ${msg}`, "error");
        } finally {
            setSyncing(null);
        }
    }

    async function loadAdoption() {
        setLoadingAdoption(true);
        try {
            const res = await axios.get(`${Base_Url}/api/appVersionAdoption`, { headers: await authHeaders() });
            setAdoption((res.data && res.data.data) || null);
        } catch (e) {
            const msg =
                (e.response && e.response.data && e.response.data.error && e.response.data.error.message) ||
                e.message || "Could not load app adoption";
            notify(msg, "error");
        } finally {
            setLoadingAdoption(false);
        }
    }

    async function loadAudienceOptions() {
        try {
            const res = await axios.get(`${Base_Url}/api/broadcastAudiences`, { headers: await authHeaders() });
            setAudienceOptions(res.data || {});
        } catch (e) {
            setAudienceOptions({});
            notify("Couldn't load audience options — you can still send to all members.", "warning");
        }
    }

    async function previewReach(a) {
        setLoadingReach(true);
        setReach(null);
        try {
            const res = await axios.post(`${Base_Url}/api/broadcastAudiencePreview`, { audience: a },
                { headers: await authHeaders() });
            setReach(res.data || null);
        } catch (e) {
            setReach(null);
        }
        setLoadingReach(false);
    }

    function currentChoice() {
        const key = audience.type === "all" ? "all"
            : audience.type === "platform" ? `platform:${audience.value}`
            : audience.type === "appVersion" ? `ver:${audience.value}`
            : audience.type === "klaviyoList" ? `klist:${audience.value}`
            : `kseg:${audience.value}`;
        return audienceChoices().find((c) => c.key === key) || { key: "all", label: "All members" };
    }

    // Grouped + filterable. A flat list of every app version and every Klaviyo
    // list/segment ran to 25+ entries with no way to search it.
    function groupedChoices() {
        const q = audienceQuery.trim().toLowerCase();
        const match = (c) => !q || c.label.toLowerCase().includes(q);
        const o = audienceOptions || {};
        const groups = [];
        const everyone = [{ key: "all", label: "All members", value: { type: "all" } }].filter(match);
        if (everyone.length) groups.push({ name: "Everyone", items: everyone });

        const plats = (o.platforms || []).map((p) => ({
            key: `platform:${p.value}`, label: `Device: ${p.value} (${p.count})`,
            value: { type: "platform", value: p.value },
        })).filter(match);
        if (plats.length) groups.push({ name: "Device", items: plats });

        const vers = (o.appVersions || []).map((v) => ({
            key: `ver:${v.value}`, label: `App version ${v.value} (${v.count})`,
            value: { type: "appVersion", value: v.value },
        })).filter(match);
        if (vers.length) groups.push({ name: "App version", items: vers });

        const lists = (o.klaviyoLists || []).map((l) => ({
            key: `klist:${l.value}`, label: `${l.name}`,
            value: { type: "klaviyoList", value: l.value },
        })).filter(match);
        if (lists.length) groups.push({ name: "Klaviyo lists", items: lists });

        const segs = (o.klaviyoSegments || []).map((g) => ({
            key: `kseg:${g.value}`, label: `${g.name}`,
            value: { type: "klaviyoSegment", value: g.value },
        })).filter(match);
        if (segs.length) groups.push({ name: "Klaviyo segments", items: segs });
        return groups;
    }

    // Flatten the picker into one list so the selection is a single choice.
    function audienceChoices() {
        const o = audienceOptions || {};
        const out = [{ key: "all", label: "All members", value: { type: "all" } }];
        (o.platforms || []).forEach((p) => out.push({
            key: `platform:${p.value}`, label: `Device: ${p.value} (${p.count})`,
            value: { type: "platform", value: p.value },
        }));
        (o.appVersions || []).slice(0, 8).forEach((v) => out.push({
            key: `ver:${v.value}`, label: `App version ${v.value} (${v.count})`,
            value: { type: "appVersion", value: v.value },
        }));
        (o.klaviyoLists || []).forEach((l) => out.push({
            key: `klist:${l.value}`, label: `Klaviyo list: ${l.name}`,
            value: { type: "klaviyoList", value: l.value },
        }));
        (o.klaviyoSegments || []).forEach((g) => out.push({
            key: `kseg:${g.value}`, label: `Klaviyo segment: ${g.name}`,
            value: { type: "klaviyoSegment", value: g.value },
        }));
        return out;
    }

    async function loadHistory() {
        setLoadingHistory(true);
        try {
            const q = query(collection(db, "Broadcasts"), orderBy("createdAt", "desc"), limit(25));
            const snap = await getDocs(q);
            setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) {
            notify("Couldn't load broadcast history.", "error");
        }
        setLoadingHistory(false);
    }

    const canSend = title.trim().length > 0 && message.trim().length > 0 && !sending;

    async function doSend() {
        setConfirmOpen(false);
        setSending(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const res = await axios.post(
                `${Base_Url}/api/sendBroadcast`,
                // 'tokens' multicasts to the fcmTokens stored on member records,
                // which reaches the app builds members already have installed and
                // returns a real delivery count. Switch to 'topic' once the mobile
                // app subscribes to all_users (a topic publish reaches nobody until
                // then, and reports no recipient count).
                { title: title.trim(), message: message.trim(), deliverVia: "tokens", audience },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const d = res.data || {};
            const sent = d.sentCount ?? 0;
            const failed = d.failureCount ?? 0;
            // A send where most deliveries failed is not a success — say so plainly
            // rather than reporting "sent" and hiding the failures in a table.
            if (failed > sent) {
                const codes = Object.entries(d.errorCounts || {})
                    .map(([c, n]) => `${c} x${n}`).join(", ");
                notify(
                    `Delivered to ${sent}, but ${failed} failed.` + (codes ? ` Errors: ${codes}` : ""),
                    "warning",
                    "Mostly undelivered"
                );
            } else {
                notify(`Broadcast sent to ${sent} members.` + (failed ? ` ${failed} failed.` : ""),
                    "success", "Broadcast sent");
            }
            setTitle("");
            setMessage("");
            loadHistory();
        } catch (err) {
            notify(
                err?.response?.data?.error?.message || err.message || "Broadcast failed",
                "error",
                "Broadcast failed"
            );
        }
        setSending(false);
    }

    const fmt = (ts) => {
        if (!ts) return "";
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return isNaN(d) ? "" : d.toLocaleString();
    };

    return (
        <Layout>
            <div style={{ padding: 24, width: "100%", maxWidth: 900 }}>
                <h2>Broadcast</h2>
                <p style={{ color: "#666", marginTop: -8 }}>
                    Sends a push notification and an in-app message to the selected audience.
                </p>

                <Label className="side-drawer-field__label">Audience</Label>
                <Combobox
                    style={{ width: "100%" }}
                    placeholder="Search audiences…"
                    freeform
                    value={audienceQuery !== "" ? audienceQuery : currentChoice().label}
                    selectedOptions={[currentChoice().key]}
                    onChange={(e) => setAudienceQuery(e.target.value)}
                    onOpenChange={(e, d) => { if (!d.open) setAudienceQuery(""); }}
                    onOptionSelect={(e, d) => {
                        const c = audienceChoices().find((x) => x.key === d.optionValue);
                        if (c) setAudience(c.value);
                        setAudienceQuery("");
                    }}
                >
                    {groupedChoices().length === 0 ? (
                        <Option key="none" value="none" disabled>No matches</Option>
                    ) : groupedChoices().map((g) => (
                        <OptionGroup key={g.name} label={g.name}>
                            {g.items.map((c) => (
                                <Option key={c.key} value={c.key}>{c.label}</Option>
                            ))}
                        </OptionGroup>
                    ))}
                </Combobox>
                {/* Reach is resolved server-side so the number reflects who will
                    actually receive a push, not just who matches the filter. */}
                <div style={{ marginTop: 6, fontSize: 13, color: "#555" }}>
                    {loadingReach ? "Checking reach…" : reach ? (
                        <>
                            <strong>{reach.reachableByPush}</strong> of {reach.members} matched members will receive a push
                            {reach.members > reach.reachableByPush && (
                                <span style={{ color: "#a33" }}> ({reach.members - reach.reachableByPush} have no device registered)</span>
                            )}
                            {reach.note && <div style={{ fontSize: 12, color: "#777" }}>{reach.note}</div>}
                        </>
                    ) : "—"}
                </div>

                <Label className="side-drawer-field__label" style={{ marginTop: 12 }}>Title</Label>
                <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Clubhouse closed Monday"
                    maxLength={80}
                    style={{ width: "100%" }}
                />

                <Label className="side-drawer-field__label" style={{ marginTop: 12 }}>Message</Label>
                <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="The message members will see."
                    maxLength={500}
                    resize="vertical"
                    style={{ width: "100%", minHeight: 100 }}
                />

                {/* Preview — what the push looks like on a device. */}
                <Label className="side-drawer-field__label" style={{ marginTop: 16 }}>Preview</Label>
                <div style={{
                    border: "1px solid #DE8925", borderRadius: 8, padding: 12,
                    background: "#fff", maxWidth: 380,
                }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {title.trim() || "Title"}
                    </div>
                    <div style={{ fontSize: 13, color: "#333", whiteSpace: "pre-wrap", marginTop: 4 }}>
                        {message.trim() || "Message body"}
                    </div>
                </div>

                <div style={{ marginTop: 16 }}>
                    <Button appearance="primary" disabled={!canSend} onClick={() => setConfirmOpen(true)}>
                        {sending ? <Spinner size="tiny" /> : "Send to all members"}
                    </Button>
                </div>

                <Divider style={{ margin: "28px 0 16px" }} />
                <h3 style={{ marginBottom: 8 }}>History</h3>
                {loadingHistory ? <Spinner size="tiny" /> : (
                    history.length === 0 ? <p style={{ color: "#666" }}>No broadcasts sent yet.</p> : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                                <thead>
                                    <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                                        <th style={{ padding: 8 }}>Sent</th>
                                        <th style={{ padding: 8 }}>Title</th>
                                        <th style={{ padding: 8 }}>Status</th>
                                        <th style={{ padding: 8 }}>Delivered</th>
                                        <th style={{ padding: 8 }}>Failed</th>
                                        <th style={{ padding: 8 }}>By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((b) => (
                                        <tr key={b.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                            <td style={{ padding: 8, whiteSpace: "nowrap" }}>{fmt(b.createdAt)}</td>
                                            <td style={{ padding: 8 }}>{b.title}</td>
                                            <td style={{ padding: 8 }}>{b.status}</td>
                                            <td style={{ padding: 8 }}>{b.sentCount ?? 0}</td>
                                            <td style={{ padding: 8 }} title={Object.entries(b.errorCounts || {}).map(([c, n]) => `${c}: ${n}`).join("\n")}>
                                                {b.failureCount ?? 0}
                                                {b.errorCounts && Object.keys(b.errorCounts).length > 0 && (
                                                    <span style={{ color: "#a33", marginLeft: 6, fontSize: 12 }}>
                                                        ({Object.entries(b.errorCounts).sort((a, c) => c[1] - a[1])[0][0].replace("messaging/", "")})
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: 8 }}>{b.createdBy}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                {/* ---- Klaviyo sync -------------------------------------------------
                    The audiences above are read live from Klaviyo, so they are only
                    as current as the last reconcile. Both passes run nightly; these
                    buttons force one now. Event RSVP segments are built from the
                    profile summaries the RSVP pass writes, which is why that one
                    matters most to marketing. */}
                <Divider style={{ margin: "32px 0 16px" }} />
                <h3 style={{ marginBottom: 4 }}>Klaviyo sync</h3>
                <p style={{ color: "#666", marginTop: 0, fontSize: 13 }}>
                    The member list and event RSVP passes also run automatically once
                    every 24 hours. Run one here if you have just changed members or
                    RSVPs and need Klaviyo to catch up rather than waiting for tonight.
                </p>
                <p style={{ color: "#666", marginTop: 0, fontSize: 13 }}>
                    Event segments is different: every new event gets its Klaviyo
                    segment the moment it is created, so this is only the catch-up for
                    events that already existed. Running it again is harmless.
                </p>
                <p style={{ color: "#666", marginTop: 0, fontSize: 13 }}>
                    Member directory is what the Connect tab in the app reads. It is
                    rebuilt automatically whenever a member changes, so this is only
                    needed to populate it for members who already existed, or if the
                    app directory looks out of date. Also harmless to re-run.
                </p>

                <Checkbox
                    checked={syncDryRun}
                    onChange={(e, d) => setSyncDryRun(!!d.checked)}
                    disabled={!!syncing}
                    label="Dry run — report what would change, write nothing"
                />

                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <Button
                        appearance="secondary"
                        disabled={!!syncing}
                        onClick={() => runSync("rsvps")}
                        icon={syncing === "rsvps" ? <Spinner size="tiny" /> : undefined}
                    >
                        {syncing === "rsvps" ? "Syncing event RSVPs…" : "Sync event RSVPs now"}
                    </Button>
                    <Button
                        appearance="secondary"
                        disabled={!!syncing}
                        onClick={() => runSync("members")}
                        icon={syncing === "members" ? <Spinner size="tiny" /> : undefined}
                    >
                        {syncing === "members" ? "Syncing member list…" : "Sync member list now"}
                    </Button>
                    <Button
                        appearance="secondary"
                        disabled={!!syncing}
                        onClick={() => runSync("eventSegments")}
                        icon={syncing === "eventSegments" ? <Spinner size="tiny" /> : undefined}
                    >
                        {syncing === "eventSegments" ? "Backfilling event segments…" : "Backfill event segments"}
                    </Button>
                    <Button
                        appearance="secondary"
                        disabled={!!syncing}
                        onClick={() => runSync("memberDirectory")}
                        icon={syncing === "memberDirectory" ? <Spinner size="tiny" /> : undefined}
                    >
                        {syncing === "memberDirectory" ? "Rebuilding member directory…" : "Rebuild member directory"}
                    </Button>
                </div>

                {syncResult ? (
                    <div
                        style={{
                            marginTop: 12,
                            border: `1px solid ${syncResult.error ? "#b00" : "#ddd"}`,
                            background: syncResult.error ? "#fff4f4" : "#fafafa",
                            borderRadius: 6,
                            padding: "10px 14px",
                            fontSize: 13,
                        }}
                    >
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                            {SYNCS[syncResult.kind].label}
                            {syncResult.dryRun ? " — dry run" : ""}
                            {" · "}
                            {syncResult.at.toLocaleTimeString()}
                        </div>
                        {syncResult.error ? (
                            <div style={{ color: "#b00" }}>{syncResult.error}</div>
                        ) : (
                            <>
                                {/* The scalar counts. `results` is the per-event list
                                    that backfillEventSegments returns and is shown as
                                    its own table below — String()-ing an array of
                                    objects here would print [object Object]. */}
                                <table>
                                    <tbody>
                                        {Object.entries(syncResult.summary)
                                            .filter(([k, v]) => !Array.isArray(v))
                                            .map(([k, v]) => (
                                                <tr key={k}>
                                                    <td style={{ padding: "1px 12px 1px 0", color: "#555" }}>{k}</td>
                                                    <td style={{ padding: "1px 0", fontWeight: 600 }}>{String(v)}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>

                                {Array.isArray(syncResult.summary.results) && syncResult.summary.results.length ? (
                                    <div style={{ marginTop: 10 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                            Per event ({syncResult.summary.results.length})
                                        </div>
                                        {/* Capped: a long season would otherwise push the
                                            rest of the screen off the page. */}
                                        <table>
                                            <tbody>
                                                {syncResult.summary.results.slice(0, 40).map((r, i) => (
                                                    <tr key={r.eventId || i}>
                                                        <td style={{ padding: "1px 12px 1px 0", color: "#555", whiteSpace: "nowrap" }}>
                                                            {r.action}
                                                        </td>
                                                        <td style={{ padding: "1px 0" }}>
                                                            {r.name || r.reason || r.segmentId || r.eventId}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {syncResult.summary.results.length > 40 ? (
                                            <div style={{ marginTop: 4, color: "#555" }}>
                                                …and {syncResult.summary.results.length - 40} more.
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </>
                        )}
                        {!syncResult.error && syncResult.dryRun ? (
                            <div style={{ marginTop: 6, color: "#555" }}>
                                Nothing was written. Untick dry run and run it again to apply.
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {/* ---- App build adoption -------------------------------------------
                    Which builds members are actually on. It sits here because the
                    audience picker above can target an app version, so the person
                    choosing one is the person who needs to know how many people
                    that is. Loaded on demand: it reads every member record. */}
                <Divider style={{ margin: "32px 0 16px" }} />
                <h3 style={{ marginBottom: 4 }}>App build adoption</h3>
                <p style={{ color: "#666", marginTop: 0, fontSize: 13 }}>
                    How many members are on the latest build and how many are behind.
                    Counts every member who has reported a version — not only those
                    reachable by push.
                </p>

                <Button
                    appearance="secondary"
                    disabled={loadingAdoption}
                    onClick={loadAdoption}
                    icon={loadingAdoption ? <Spinner size="tiny" /> : undefined}
                >
                    {loadingAdoption ? "Checking…" : adoption ? "Refresh" : "Check app adoption"}
                </Button>

                {adoption ? (
                    <div style={{ marginTop: 12, fontSize: 13 }}>
                        <div style={{ marginBottom: 8 }}>
                            <strong>{adoption.pctOnCurrent}%</strong> on {adoption.current || "the latest build"}
                            {" · "}
                            <strong>{adoption.pctBehind}%</strong> on an older build
                            {adoption.ahead ? <> · {adoption.ahead} on a newer build</> : null}
                        </div>
                        <table className="table" style={{ width: "100%", maxWidth: 460 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "left" }}>Build</th>
                                    <th style={{ textAlign: "right" }}>Members</th>
                                    <th style={{ textAlign: "right" }}>Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(adoption.versions || []).map((v) => (
                                    <tr key={v.version} style={v.version === adoption.current ? { fontWeight: 600 } : undefined}>
                                        <td>{v.version}{v.version === adoption.current ? " (current)" : ""}</td>
                                        <td style={{ textAlign: "right" }}>{v.members}</td>
                                        <td style={{ textAlign: "right" }}>{v.pct}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Stated, not hidden: these members have the app but have never
                            reported a version, so they are excluded from the percentages
                            rather than counted as up to date. */}
                        <div style={{ marginTop: 8, color: "#555" }}>
                            Percentages are of the {adoption.reportingAVersion} member(s) reporting a build,
                            out of {adoption.members} total.
                            {adoption.noVersionRecorded ? (
                                <> {adoption.noVersionRecorded} member(s) have never reported one — they are
                                not counted as up to date.</>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {/* Mass send is irreversible — confirm, and restate the reach. */}
                <Dialog open={confirmOpen} onOpenChange={(e, data) => setConfirmOpen(data.open)}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Send to every member?</DialogTitle>
                            <DialogContent>
                                <p>
                                    This sends a push notification and an in-app message to{" "}
                                    <strong>{currentChoice().label}</strong>
                                    {reach ? <> — <strong>{reach.reachableByPush}</strong> device(s)</> : null}.
                                    It cannot be recalled.
                                </p>
                                <div style={{ border: "1px solid #ddd", borderRadius: 6, padding: 10, marginTop: 8 }}>
                                    <div style={{ fontWeight: 600 }}>{title.trim()}</div>
                                    <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{message.trim()}</div>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button appearance="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                                <Button appearance="primary" onClick={doSend}>Send now</Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            </div>
        </Layout>
    );
}
